/**
 * ThumbnailScaleCalculator
 * 
 * Calculates thumbnail image scaling for circle-bound content.
 * Converts 3D circle radius to 2D pixel radius using camera projection math.
 * Combines perspective distance scaling with circle-fit scaling.
 */

import { quaternionToMatrix } from './cameraProjection'
import { distance } from './vectorMath'

interface Camera {
  intrinsics: { width: number; height: number; fx?: number; fy?: number; cx?: number; cy?: number }
  position: [number, number, number]
  rotation?: [number, number, number, number]  // [qw, qx, qy, qz]
  [key: string]: any
}

interface ScaleCalculationResult {
  circleScale: number
  distanceScale: number
  circleRadiusPx: number
  depth: number
  focalLength: number
}

/**
 * Helper: Multiply 3x3 matrix by 3D vector
 */
function matrixVecMultiply(matrix: number[][], vec: [number, number, number]): [number, number, number] {
  return [
    matrix[0][0] * vec[0] + matrix[0][1] * vec[1] + matrix[0][2] * vec[2],
    matrix[1][0] * vec[0] + matrix[1][1] * vec[1] + matrix[1][2] * vec[2],
    matrix[2][0] * vec[0] + matrix[2][1] * vec[1] + matrix[2][2] * vec[2]
  ]
}

/**
 * Calculate depth (Z-distance) from 3D world point to camera in camera space
 * 
 * Uses the same transformation as project3DPointToCamera (validated correct):
 *   P_cam = R @ P + T
 * 
 * Where R is the rotation matrix from quaternion (direct, not transposed)
 * and T is the camera position/translation.
 * 
 * @param point3D - 3D point in world coordinates [x, y, z]
 * @param camera - Camera with position and rotation (quaternion)
 * @returns Depth (Z-distance) in meters, positive = in front of camera
 */
export function calculateCameraSpaceDepth(point3D: [number, number, number], camera: Camera): number {
  if (!camera.rotation) {
    // Fallback: use Euclidean distance if no rotation available
    return distance(camera.position, point3D)
  }

  // Get rotation matrix from quaternion (direct, NOT transposed)
  // This matches the validated project3DPointToCamera formula
  const R = quaternionToMatrix(camera.rotation)
  const T = camera.position

  // Transform to camera space: P_cam = R @ P + T
  const P_cam = matrixVecMultiply(R, point3D)
  P_cam[0] += T[0]
  P_cam[1] += T[1]
  P_cam[2] += T[2]

  // Z component is the depth
  const depth = P_cam[2]

  return Math.max(depth, 0.1)  // Clamp to minimum 0.1m to avoid division issues
}

/**
 * Calculate focal length from camera intrinsics
 * Uses average of fx and fy, or single focal length if available
 * 
 * @param camera - Camera with intrinsics
 * @returns Focal length in pixels
 */
export function calculateFocalLength(camera: Camera): number {
  const intrinsics = camera.intrinsics || {}
  
  // COLMAP stores focal length as fx, fy (may differ for non-square pixels)
  if (intrinsics.fx && intrinsics.fy) {
    return (intrinsics.fx + intrinsics.fy) / 2
  }
  
  // Fallback: assume focal length is ~1.2x image width (typical for camera optics)
  return intrinsics.width ? intrinsics.width * 1.2 : 1000
}

/**
 * Convert 3D sphere radius to 2D image pixel radius at a specific camera
 * 
 * Formula:
 *   radius_2d = (radius_3d * focal_length) / depth
 * 
 * Where:
 * - radius_3d: 3D sphere radius in world coordinates (meters)
 * - focal_length: Camera focal length in pixels (fx or fy)
 * - depth: Z-distance from camera to center of sphere (meters)
 * 
 * This uses perspective projection: objects farther away appear smaller.
 * 
 * @param point3D - Center of 3D sphere in world coordinates
 * @param radiusMeters - Radius of 3D sphere in meters
 * @param camera - Camera with position, rotation, and intrinsics
 * @returns Circle radius in pixels (clamped to 1.0 minimum)
 */
export function calculateCircleRadiusAtCamera(
  point3D: [number, number, number],
  radiusMeters: number,
  camera: Camera
): number {
  if (!radiusMeters || radiusMeters <= 0) {
    return 0
  }

  const depth = calculateCameraSpaceDepth(point3D, camera)
  const focalLength = calculateFocalLength(camera)

  // Apply perspective projection formula
  const radiusPx = (radiusMeters * focalLength) / depth

  return Math.max(radiusPx, 1.0)  // Minimum 1px to avoid invisible circles
}

/**
 * Calculate how much to scale a thumbnail image to make circle content fill viewport
 * 
 * Adaptive scaling strategy:
 * - Small circles (< viewport): Zoom IN to make circle fill viewport (target = thumbnailWidth / 2)
 * - Large circles (> viewport): Zoom OUT to fit circle in viewport (target = thumbnailWidth / 2)
 * 
 * Formula: scale = (thumbnailWidth / 2) / circleRadiusPx
 * This ensures circle always fits within thumbnail viewport regardless of projected size.
 * 
 * @param thumbnailWidth - Thumbnail display width in pixels
 * @param circleRadiusPx - Circle radius at this camera in pixels (projected from 3D)
 * @returns Scale factor to apply to image (0.01x to 20x range)
 */
export function calculateCircleScale(thumbnailWidth: number, circleRadiusPx: number): number {
  if (!circleRadiusPx || circleRadiusPx <= 0) {
    return 1.0
  }

  // Make circle radius = half thumbnail width (circle diameter = thumbnail width)
  // This fits the circle perfectly within the viewport
  const targetRadius = thumbnailWidth / 2
  const scale = targetRadius / circleRadiusPx

  // Allow wide range: 0.01x (very large circles) to 20x (very small circles)
  // No minimum clamp - let it zoom out as needed for large projected circles
  return Math.max(0.01, Math.min(scale, 20.0))
}

/**
 * Calculate distance-based perspective scaling
 * 
 * Farther cameras see the point smaller, so we enlarge the thumbnail
 * to compensate and show detail.
 * 
 * Formula: scale = distance from first camera
 * 
 * @param cameraDistance - 3D distance from camera to point (meters)
 * @returns Perspective scale factor
 */
export function calculateDistanceScale(cameraDistance: number): number {
  // Clamp to reasonable range: 0.5x to 5x
  return Math.max(0.5, Math.min(cameraDistance, 5.0))
}

/**
 * Calculate both circle scale and distance scale for comparison
 * 
 * This allows visual comparison in logs and future A/B testing:
 * - circleScale: Fits circle content to thumbnail viewport
 * - distanceScale: Compensates for perspective (farther = bigger)
 * 
 * Currently uses ONLY circleScale for final transform.
 * Log both to understand how scales differ.
 * 
 * @param point3D - 3D sphere center in world coordinates
 * @param circleRadiusMeters - Sphere radius in meters (3D)
 * @param thumbnailWidth - Thumbnail display width in pixels
 * @param camera - Camera with position, rotation, intrinsics
 * @returns Object with both scales and diagnostic data
 */
export function calculateScales(
  point3D: [number, number, number],
  circleRadiusMeters: number | undefined,
  thumbnailWidth: number,
  camera: Camera
): ScaleCalculationResult {
  const depth = calculateCameraSpaceDepth(point3D, camera)
  const focalLength = calculateFocalLength(camera)
  const cameraDistance = distance(camera.position, point3D)

  // Circle scale: fits 3D sphere to thumbnail
  let circleRadiusPx = 0
  let circleScale = 1.0

  if (circleRadiusMeters && circleRadiusMeters > 0) {
    circleRadiusPx = calculateCircleRadiusAtCamera(point3D, circleRadiusMeters, camera)
    circleScale = calculateCircleScale(thumbnailWidth, circleRadiusPx)
  }

  // Distance scale: perspective compensation
  const distanceScale = calculateDistanceScale(cameraDistance)

  return {
    circleScale,
    distanceScale,
    circleRadiusPx,
    depth,
    focalLength
  }
}

/**
 * Generate imageTransform CSS string for thumbnail
 * 
 * Combines translation (center point) and scaling (circle fit).
 * 
 * @param offsetX - X translation in pixels
 * @param offsetY - Y translation in pixels
 * @param scale - Scale factor to apply
 * @returns CSS transform string
 */
export function generateImageTransform(offsetX: number, offsetY: number, scale: number): string {
  return `translate(${offsetX}px, ${offsetY}px) scale(${scale})`
}
